import type { CSSProperties } from "react";
import { DEFAULT_LOMDA_STYLE } from "@/features/lomdaEditorV2/lib/styleUtils";
import { isVideoUrl } from "@/features/lomdaEditorV2/lib/mediaAssetUtils";

/** @deprecated Use DEFAULT_LOMDA_STYLE.background from styleUtils instead. */
export const DEFAULT_SCENE_BACKGROUND = DEFAULT_LOMDA_STYLE.background;

/** True if value is a CSS background image url(...). */
export function isCssBackgroundImage(value: string): boolean {
    return value.trim().startsWith("url(");
}

/** True if value is a CSS gradient (linear, radial, or conic). */
export function isCssGradient(value: string): boolean {
    return /^(linear-gradient|radial-gradient|conic-gradient)\s*\(/i.test(
        value.trim()
    );
}

/** True if value is a CSS url() value. Alias for isCssBackgroundImage. */
export const isCssUrl = isCssBackgroundImage;

/**
 * Extracts URL from url("...") or url('...') in scene.style.background.
 * Returns null if value is not a url().
 */
export function parseBackgroundMediaUrl(
    value: string | null | undefined
): string | null {
    const v = typeof value === "string" ? value.trim() : "";
    if (!v.startsWith("url(")) return null;
    const inner = v
        .slice(4)
        .replace(/\)\s*$/, "")
        .trim();
    const unquoted = inner.replace(/^["']|["']$/g, "").trim();
    return unquoted || null;
}

/**
 * True when user hasn't set a custom background (empty or default color).
 * When false, prefer style.background (from Background sidebar) over scene.imageUrl.
 */
export function isDefaultSidebarBackground(
    sceneBgValue: string | null | undefined
): boolean {
    const hasSidebarBackground =
        sceneBgValue != null && String(sceneBgValue).trim() !== "";
    return (
        !hasSidebarBackground ||
        (typeof sceneBgValue === "string" &&
            sceneBgValue.trim().toLowerCase() ===
                DEFAULT_SCENE_BACKGROUND.toLowerCase())
    );
}

/**
 * Returns longhand CSS style for scene background (color, gradient, or url() for images).
 * Video URLs in url() are not supported by CSS background-image; return {} so caller renders <video>.
 * Uses only longhand properties to avoid React warning about mixing shorthand and longhand.
 */
export function getSceneBackgroundStyle(
    value: string | null | undefined
): CSSProperties {
    const v = typeof value === "string" ? value.trim() : "";
    if (!v) return {};

    if (v.startsWith("url(")) {
        const url = parseBackgroundMediaUrl(v);
        if (url && isVideoUrl(url)) return {};
        return {
            backgroundImage: v,
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
        };
    }
    if (/^(linear-gradient|radial-gradient|conic-gradient)\s*\(/i.test(v)) {
        return { backgroundImage: v };
    }
    return { backgroundColor: v };
}
